import { useAppDispatch } from '../../stores/redux.store';
import { resetAppState } from '../../actions/app-actions';
import { useLoaderData, useLocation } from 'react-router-dom';
import { buildInitialState, PreloadedAppState } from '../init-app-state';
import { useEffect, useLayoutEffect } from 'react';
import hasNoInjectedServerState from '../has-no-injected-server-state';
import { fetchApi, ServerErrorException } from '../fetch-api';
import { captureErrorMessage, captureTaggedException } from '../analytics/error-reporting';
import { setServerError } from '../../actions/server-error-actions';
import { Action, ServerError } from '../../types';
import { isSasStateEndpointEnabled } from './usePageState';
import { checkFeatureGateOnceInitialized } from '../feature-flags/feature-gate';
import { AidFrontendFeatureGate } from '../../providers/FeatureGates';
import { useNavigateSoftOrHard } from './useNavigateSoftOrHard';
import { usePrevious } from './usePrevious';
import { Capability } from '../analytics/capability-tags';

export const setGlobalStateLoading = (isLoading: boolean): Action => ({
  type: 'SET_GLOBAL_STATE_LOADING',
  isLoading,
});

const SAS_FAST_FIVE_FETCH_HEADER = 'sas-fast-five-fetch';
const EXCLUDED_PATHS = {
  existingSasMilestone2StateRoutes: ['/login', '/signup/welcome', '/signup/verify-email/otp/'],
  previousReduxStore: ['/2step/enrollment/', '/signup/welcome/p/loom-create-workspace'],
} as const;

/**
 * This hook is used on each new page load to fetch the initial state of the app from the
 * server and refresh the full redux state with that data.
 */
export const useGlobalPageState = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigateSoftOrHard();

  useEffect(() => {
    const fetchData = async () => {
      if (await checkFeatureGateOnceInitialized(AidFrontendFeatureGate.USE_STATE_DATA_LOADERS)) {
        return;
      }

      // For non-bifrost pages (i.e. injected state from sign-in-service), use the existing non-sas
      // behaviour. This could mean we may have old injected state which doesn't get
      // refreshed from here on page navigation, but that matches existing behaviour
      const hasInjectedServerState = !hasNoInjectedServerState();
      if (hasInjectedServerState) {
        return;
      }

      const alreadyFetchesStateSpecificToRoute =
        isSasStateEndpointEnabled() &&
        EXCLUDED_PATHS.existingSasMilestone2StateRoutes.some(path => location.pathname === path);
      const requiresStateFromPreviousReduxStore = EXCLUDED_PATHS.previousReduxStore.some(path =>
        location.pathname.startsWith(path)
      );
      if (requiresStateFromPreviousReduxStore || alreadyFetchesStateSpecificToRoute) {
        return;
      }

      dispatch(setGlobalStateLoading(true));

      try {
        const url = location.pathname + location.search;
        const data = await fetchFast5GlobalPageState(url);

        if (isRedirect(data)) {
          navigate(data.redirectTo);
          return;
        }

        const fullState = await buildInitialState(data);
        dispatch(resetAppState(fullState.initialStoreState));
      } catch (error) {
        let errorType: ServerError = { type: 'internal_server_error' };
        if (error instanceof ServerErrorException) {
          errorType = error.errorType;
          captureErrorMessage(`Failed to load AppState in Bifrost: ${error.message}`);
        } else {
          captureErrorMessage(`Unexpected error while loading AppState in Bifrost: ${error}`);
        }
        dispatch(setServerError(errorType));
      }

      dispatch(setGlobalStateLoading(false));
    };

    fetchData();

    // re-fetch whenever we change page, i.e. the fixed route
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
};

type ServerSideRedirect = { redirectTo: string };

export const isRedirect = (
  data: PreloadedAppState | ServerSideRedirect | null
): data is ServerSideRedirect => Boolean(data && 'redirectTo' in data);

export const fetchFast5GlobalPageState = async (
  fullPageUrl: string
): Promise<PreloadedAppState | ServerSideRedirect | null> => {
  const headers = {
    [SAS_FAST_FIVE_FETCH_HEADER]: 'true',
  };
  const response = await fetchApi(fullPageUrl, headers);
  const data = await response.json();

  return data;
};

export const fetchPageState = async (
  restStateEndpoint: string,
  request: Request,
  tags: {
    capabilities: Capability[];
  }
): Promise<PreloadedAppState | ServerSideRedirect | null> => {
  const url = new URL(request.url);
  const queryParams = url.searchParams.size > 0 ? `?${url.searchParams.toString()}` : '';

  const response = await fetchApi(restStateEndpoint + queryParams);

  if (response.status === 404 || response.status >= 500) {
    captureTaggedException('Error fetching page state', {
      capabilities: tags.capabilities,
      extraProperties: {
        restStateEndpoint,
        responseStatus: response.status,
      },
    });
  }

  return await response.json();
};

type Opts = {
  /**
   * @deprecated - needed only to restore a Loom social signup flow that relies on some page state
   * data but specifically needs to avoid redirects (which Loom handle themsleves)
   * */
  skipRedirect?: boolean;
};

// Later we will use this to extract page state, but for now we just sync with Redux, same as useGlobalPageState hook
export const usePageStateLoader = (opts?: Opts) => {
  const location = useLocation();
  const previousLocation = usePrevious(location.pathname);

  const data = useLoaderData() as PreloadedAppState | ServerSideRedirect;
  const dispatch = useAppDispatch();

  useLayoutEffect(() => {
    if (previousLocation === location.pathname) {
      // Component re-renders can cause this hook to reset the Redux app state unnecessarily.
      // This condition ensures we only ever fetch state and reset Redux when only the pathname
      // and not the search, nor other route states, changes.
      return;
    }

    // No data should mean Feature Gate is disabled and useGlobalPageState is handling the state
    if (!data) {
      // If Feature Gate is enabled but there is no data, we should log to Sentry?
      // TODO: throw descriptive error here for handling in fine-grained error boundaries KIRBY-9124
      return;
    }

    if (isRedirect(data)) {
      if (opts?.skipRedirect) return;

      window.location.assign(data.redirectTo);
      dispatch(setGlobalStateLoading(true));
      return;
    }

    buildInitialState(data)
      .then(fullState => {
        dispatch(resetAppState(fullState.initialStoreState));
      })
      .catch(error => {
        let errorType: ServerError = { type: 'internal_server_error' };
        if (error instanceof ServerErrorException) {
          errorType = error.errorType;
          captureErrorMessage(`Failed to load AppState in Bifrost: ${error.message}`);
        } else {
          captureErrorMessage(`Unexpected error while loading AppState in Bifrost: ${error}`);
        }
        dispatch(setServerError(errorType));
      });
  }, [data, dispatch, location.pathname, previousLocation, opts?.skipRedirect]);
};
